/**
 * Утилиты для формирования AppsFlyer OneLink.
 */

import type { AppsFlyerParams } from '@/utils/appsFlyerParams';
import type { AttParams } from '@/utils/attParams';

const APPSFLYER_BASE_URL = 'https://shieldix.onelink.me/GiOH';
const DEFAULT_APPSFLYER_MEDIA_SOURCE = 'website';
const DEFAULT_DEEP_LINK_VALUE = 'extra_onboarding';
const APPSFLYER_FALLBACK_DEEPLINK = 'shieldix://';

export type PaymentHandoffMethod = 'card' | 'apple_pay';

const PAYMENT_HANDOFF_METHOD_VALUES: Record<PaymentHandoffMethod, string> = {
  card: 'card',
  apple_pay: 'applepay',
};

export interface AppsFlyerDeeplink {
  attParams?: AttParams | null;
  handoffToken?: string;
  isShort?: boolean;
  link?: string;
  nVersion?: string;
  paymentMethod?: PaymentHandoffMethod;
  paywallVersion?: string;
  reviews?: boolean;
  video?: string;
}

interface AppsFlyerDeeplinkFromPathOptions
  extends Omit<AppsFlyerDeeplink, 'paywallVersion'> {
  includePaywall?: boolean;
}

function getNonEmptyValue(value: string | undefined): string | undefined {
  return value?.trim() ? value : undefined;
}

function getAttPaywallQuery(attParams?: AttParams | null): string {
  if (!attParams) {
    return '';
  }

  const query = new URLSearchParams();
  const source = getNonEmptyValue(attParams.att_source);
  const sourceId = getNonEmptyValue(attParams.att_sourceId)
    ?? getNonEmptyValue(attParams.att_source_id);
  const sourceCid = getNonEmptyValue(attParams.att_sourceCid)
    ?? getNonEmptyValue(attParams.att_source_cid);

  if (source) {
    query.set('att_source', source);
  }
  if (sourceId) {
    query.set('att_sourceId', sourceId);
  }
  if (sourceCid) {
    query.set('att_sourceCid', sourceCid);
  }

  return query.toString();
}

function buildPaywallDeepLinkValue(
  paywallVersion: string,
  attParams?: AttParams | null,
): string {
  const attQuery = getAttPaywallQuery(attParams);
  return attQuery ? `${paywallVersion}?${attQuery}` : paywallVersion;
}

function hasDeepLinkPayload(deeplink: AppsFlyerDeeplink): boolean {
  return Boolean(
    deeplink.nVersion
    || deeplink.paywallVersion
    || deeplink.handoffToken
    || deeplink.reviews !== undefined
    || deeplink.isShort !== undefined
    || deeplink.link
    || deeplink.video
    || deeplink.paymentMethod,
  );
}

/**
 * Извлекает версию paywall из URL или возвращает дефолтную.
 */
export function getPaywallVersionFromPath(pathname: string): string {
  const match = pathname.match(/\/paywall\/(v\d+)/);
  if (match && match[1]) {
    return match[1];
  }
  return 'v3';
}

export function buildAppsFlyerDeeplink(
  deeplink: AppsFlyerDeeplink = {},
): AppsFlyerDeeplink {
  return deeplink;
}

export function buildAppsFlyerRedirectUrl(
  afParams?: AppsFlyerParams | null,
  deeplink: AppsFlyerDeeplink = {},
  fallbackCampaign?: string,
): string {
  const url = new URL(APPSFLYER_BASE_URL);

  if (afParams) {
    Object.entries(afParams).forEach(([key, value]) => {
      if (key !== 'af_dp' && value.trim()) {
        url.searchParams.set(key, value);
      }
    });
  }

  if (!url.searchParams.get('pid')?.trim()) {
    url.searchParams.set('pid', DEFAULT_APPSFLYER_MEDIA_SOURCE);
  }
  if (!url.searchParams.get('c')?.trim() && fallbackCampaign) {
    url.searchParams.set('c', fallbackCampaign);
  }

  if (hasDeepLinkPayload(deeplink)) {
    url.searchParams.set('deep_link_value', DEFAULT_DEEP_LINK_VALUE);
  }
  if (deeplink.nVersion) {
    url.searchParams.set('deep_link_sub1', deeplink.nVersion);
  }
  if (deeplink.paywallVersion) {
    url.searchParams.set(
      'deep_link_sub2',
      buildPaywallDeepLinkValue(deeplink.paywallVersion, deeplink.attParams),
    );
  }
  if (deeplink.handoffToken) {
    url.searchParams.set('deep_link_sub3', deeplink.handoffToken);
  }
  if (deeplink.reviews !== undefined) {
    url.searchParams.set('deep_link_sub4', String(deeplink.reviews));
  }
  if (deeplink.isShort !== undefined) {
    url.searchParams.set('deep_link_sub5', String(deeplink.isShort));
  }
  if (deeplink.link) {
    url.searchParams.set('deep_link_sub6', deeplink.link);
  }
  if (deeplink.video) {
    url.searchParams.set('deep_link_sub7', deeplink.video);
  }
  if (deeplink.paymentMethod) {
    url.searchParams.set(
      'deep_link_sub8',
      PAYMENT_HANDOFF_METHOD_VALUES[deeplink.paymentMethod],
    );
  }

  url.searchParams.set('af_dp', APPSFLYER_FALLBACK_DEEPLINK);
  return url.toString();
}

export function buildAppsFlyerDeeplinkFromPath(
  pathname: string,
  {
    includePaywall = true,
    ...deeplink
  }: AppsFlyerDeeplinkFromPathOptions = {},
): AppsFlyerDeeplink {
  return buildAppsFlyerDeeplink({
    ...deeplink,
    paywallVersion: includePaywall
      ? getPaywallVersionFromPath(pathname)
      : undefined,
  });
}
